import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Returns the authoritative cart item count from a cart update event.
 * Prefers the cart API resource, then explicit event data.
 * @param {CartUpdateEvent} event
 * @returns {number | undefined}
 */
function getAuthoritativeItemCount(event) {
  const resourceCount = event.detail?.resource?.item_count;
  if (typeof resourceCount === 'number') return resourceCount;

  const dataCount = event.detail?.data?.itemCount;
  if (typeof dataCount === 'number') return dataCount;

  return undefined;
}

/**
 * Updates every cart bubble count element in the document.
 * @param {number} itemCount
 */
function setCartCountOnAllBubbles(itemCount) {
  for (const countElement of document.querySelectorAll('[data-testid="cart-bubble"]')) {
    if (!(countElement instanceof HTMLElement)) continue;

    countElement.textContent = itemCount < 100 ? String(itemCount) : '';
    countElement.classList.toggle('hidden', itemCount === 0);

    countElement.closest('.cart-bubble')?.classList.toggle('visually-hidden', itemCount === 0);
  }

  for (const cartIcon of document.querySelectorAll('cart-icon')) {
    cartIcon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
  }
}

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.#syncCartCountFromServer();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (event.detail?.data?.didError && typeof event.detail?.resource?.item_count !== 'number') return;

    const itemCount = getAuthoritativeItemCount(event);
    if (itemCount === undefined) return;

    this.renderCartBubble(itemCount);
  };

  /**
   * Renders the cart bubble count across all cart bubbles.
   * @param {number} itemCount - The authoritative number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the header bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    setCartCountOnAllBubbles(itemCount);

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Fetches the current cart and syncs all bubble counts after bfcache restore.
   */
  #syncCartCountFromServer = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;

      const cart = await response.json();
      if (typeof cart.item_count !== 'number') return;

      this.renderCartBubble(cart.item_count, false);
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
